'use client';

import { useState } from 'react';

interface CodeBlockProps {
  /** Le code affiché, tel qu'il doit être collé dans un terminal. */
  code: string;
  /** Étiquette de langage affichée en haut à gauche (`bash`, `json`, `python`). */
  lang?: string;
  /** Légende optionnelle : à quoi sert ce bloc. */
  caption?: string;
}

export default function CodeBlock({ code, lang, caption }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Presse-papiers refusé (contexte non sécurisé, permission) : le code
      // reste sélectionnable à la main, il n'y a rien à signaler de plus.
    }
  };

  return (
    <figure className="my-4">
      <div className="rounded-lg border border-slate-800 bg-slate-900 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2 border-b border-slate-800 bg-slate-950">
          <span className="text-xs font-mono uppercase tracking-wider text-slate-400">
            {lang || 'text'}
          </span>
          <button
            type="button"
            onClick={copy}
            className="text-xs font-medium text-slate-300 hover:text-white transition-colors px-2 py-1 rounded hover:bg-slate-800"
          >
            {copied ? '✓ Copié' : 'Copier'}
          </button>
        </div>
        <div className="overflow-x-auto">
          <pre className="p-4 text-sm leading-relaxed">
            <code className="font-mono text-slate-100 whitespace-pre">{code}</code>
          </pre>
        </div>
      </div>
      {caption ? (
        <figcaption className="mt-2 text-sm text-slate-500 dark:text-slate-400">{caption}</figcaption>
      ) : null}
    </figure>
  );
}
